import type { Container } from 'pixi.js';
import type { LastAction } from '../../../characters';
import type { RawPiece } from '@blind-kings/game-core';
import { getSkinPack } from '../../../characters';
import { cellOrigin } from './Highlights';
import type { PieceNode } from './PieceNode';

function easeOutCubic(t: number) {
  return 1 - (1 - t) ** 3;
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function boardHasPiece(board: RawPiece[][], pieceId: number): boolean {
  for (const row of board) {
    for (const cell of row) {
      if (cell?.id === pieceId) return true;
    }
  }
  return false;
}

export type ActionAnimatorOptions = {
  piecesLayer: Container;
  getPieceNode: (id: number) => PieceNode | undefined;
  getLayout: () => {
    cellSize: number;
    cols: number;
    rows: number;
    renderVertically: boolean;
  };
  rebuildFromBoard: (board: RawPiece[][]) => Promise<void>;
  skinId?: string;
};

export class ActionAnimator {
  private busy = false;
  private cancelled = false;
  private skinId: string;

  constructor(private opts: ActionAnimatorOptions) {
    this.skinId = opts.skinId ?? 'classic';
  }

  get isAnimating() {
    return this.busy;
  }

  setSkinId(skinId: string) {
    this.skinId = skinId;
  }

  cancel() {
    this.cancelled = true;
  }

  async play(action: LastAction, settleBoard: RawPiece[][]): Promise<void> {
    if (this.busy) {
      await this.opts.rebuildFromBoard(settleBoard);
      return;
    }
    this.busy = true;
    this.cancelled = false;

    try {
      if (prefersReducedMotion()) {
        await this.opts.rebuildFromBoard(settleBoard);
        return;
      }

      if (action.type === 'flip') {
        await this.playFlip(action, settleBoard);
      } else if (action.type === 'move') {
        await this.playMove(action, settleBoard);
      } else {
        await this.playAttack(action, settleBoard);
      }
    } catch {
      await this.opts.rebuildFromBoard(settleBoard);
    } finally {
      this.busy = false;
    }
  }

  private async playFlip(action: LastAction, settleBoard: RawPiece[][]) {
    const pack = getSkinPack(this.skinId);
    const { pulse, reveal, cooldown } = pack.interactions.flip.timings;
    const node = this.opts.getPieceNode(action.pieceId);
    if (!node) {
      await this.opts.rebuildFromBoard(settleBoard);
      return;
    }

    node.setPulsing(true);
    await this.wait(pulse);
    if (this.cancelled) return;
    node.setPulsing(false);

    const layout = this.opts.getLayout();
    await node.setPieceIdentity(action.pieceValue, action.pieceTeamIndex, layout.cellSize);
    await node.setAnimState('revealing', { loop: false, cellSize: layout.cellSize });
    await this.wait(reveal);
    if (this.cancelled) return;
    await node.returnToAmbient(layout.cellSize);

    await this.opts.rebuildFromBoard(settleBoard);
    await this.wait(cooldown);
  }

  private async playMove(action: LastAction, settleBoard: RawPiece[][]) {
    const pack = getSkinPack(this.skinId);
    const { windup, depart, travel, land, cooldown } = pack.interactions.move.timings;
    const node = this.opts.getPieceNode(action.pieceId);
    if (!node) {
      await this.opts.rebuildFromBoard(settleBoard);
      return;
    }

    const layout = this.opts.getLayout();
    this.opts.piecesLayer.setChildIndex(node, this.opts.piecesLayer.children.length - 1);

    await node.setAnimState('moving', { loop: true, cellSize: layout.cellSize });
    await this.wait(windup + depart);
    if (this.cancelled) return;

    await this.tweenNodeTo(node, action.to, travel);
    if (this.cancelled) return;

    await this.wait(land);
    if (this.cancelled) return;
    await node.returnToAmbient(layout.cellSize);

    await this.opts.rebuildFromBoard(settleBoard);
    await this.wait(cooldown);
  }

  private async playAttack(action: LastAction, settleBoard: RawPiece[][]) {
    const pack = getSkinPack(this.skinId);
    const { windup, depart, travel, clash, resolve, cooldown } =
      pack.interactions.attack.timings;
    const node = this.opts.getPieceNode(action.pieceId);
    const target =
      action.targetPieceId !== undefined
        ? this.opts.getPieceNode(action.targetPieceId)
        : undefined;

    if (!node) {
      await this.opts.rebuildFromBoard(settleBoard);
      return;
    }

    const layout = this.opts.getLayout();
    this.opts.piecesLayer.setChildIndex(node, this.opts.piecesLayer.children.length - 1);
    if (target) target.alpha = 0.85;

    await node.setAnimState('attacking', {
      loop: true,
      cellSize: layout.cellSize,
    });

    await this.wait(windup + depart);
    if (this.cancelled) return;

    await this.tweenNodeTo(node, action.to, travel);
    if (this.cancelled) return;

    const prev = node.scale.x;
    node.scale.set(prev * 1.15);
    if (target) target.alpha = 0.3;
    // Replay the attack clip once at clash so the strike lands on-beat
    await Promise.all([
      node.playAnimOnce('attacking', layout.cellSize),
      this.wait(clash),
    ]);
    if (this.cancelled) return;
    node.scale.set(prev);

    const attackerLives = boardHasPiece(settleBoard, action.pieceId);
    const targetLives =
      action.targetPieceId === undefined ||
      boardHasPiece(settleBoard, action.targetPieceId);

    if (target) {
      target.alpha = 1;
      if (targetLives) {
        await target.returnToAmbient(layout.cellSize);
      } else {
        await target.playAnimOnce('dead', layout.cellSize);
        if (this.cancelled) return;
      }
    }

    if (attackerLives) {
      await node.returnToAmbient(layout.cellSize);
    } else {
      await node.playAnimOnce('dead', layout.cellSize);
      if (this.cancelled) return;
    }

    await this.wait(resolve);
    if (this.cancelled) return;

    await this.opts.rebuildFromBoard(settleBoard);
    await this.wait(cooldown);
  }

  private tweenNodeTo(
    node: PieceNode,
    to: { x: number; y: number },
    duration: number
  ): Promise<void> {
    const layout = this.opts.getLayout();
    const dest = cellOrigin(
      to.x,
      to.y,
      layout.cellSize,
      layout.cols,
      layout.rows,
      layout.renderVertically
    );
    const startX = node.x;
    const startY = node.y;
    const endX = dest.centerX;
    const endY = dest.centerY;
    const start = performance.now();

    return new Promise((resolve) => {
      const tick = () => {
        if (this.cancelled) {
          resolve();
          return;
        }
        const raw = Math.min(1, (performance.now() - start) / duration);
        const t = easeOutCubic(raw);
        node.position.set(startX + (endX - startX) * t, startY + (endY - startY) * t);
        if (raw < 1) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });
  }

  private wait(ms: number) {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
}
